import React, { useEffect, useMemo, useState } from "react";
import { Input, Popover, Select } from "antd";
import { WarningOutlined, SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import GetTaskHeatmapApi from "../../api/tasks/GetTaskHeatmapApi";
import PageLoader from "../sub/PageLoader";
import moment from "moment";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Performance tier color system:
 * 0-9%:    Rose-50 bg, rose-700 text (Critical Low)
 * 10-29%:  Rose-100 bg, rose-800 text (Substantial Delay)
 * 30-39%:  Rose-600 solid bg, white text (High Alert)
 * 40-59%:  Amber-100 bg, amber-900 text (Moderate)
 * 60-69%:  Amber-400 bg, slate-950 text (Accelerating)
 * 70-89%:  Emerald-100 bg, emerald-900 text (On Track)
 * 90-100%: Emerald-600 solid bg, white text (Excellence)
 */
function getCellStyles(percentage) {
  if (percentage === null || percentage === undefined) {
    return { bg: "transparent", border: "transparent", color: "#94a3b8" };
  }
  // 0-9%: Darkest red
  if (percentage <= 9) {
    return { bg: "#991b1b", border: "#7f1d1d", color: "#ffffff" };
  }
  // 10-19%: Dark red
  if (percentage <= 19) {
    return { bg: "#dc2626", border: "#b91c1c", color: "#ffffff" };
  }
  // 20-29%: Medium red
  if (percentage <= 29) {
    return { bg: "#ef4444", border: "#dc2626", color: "#ffffff" };
  }
  // 30-39%: Light red
  if (percentage <= 39) {
    return { bg: "#fca5a5", border: "#f87171", color: "#7f1d1d" };
  }
  // 40-59%: Amber
  if (percentage <= 59) {
    return { bg: "#fef3c7", border: "#fde68a", color: "#78350f" };
  }
  // 60-69%: Gold
  if (percentage <= 69) {
    return { bg: "#fbbf24", border: "#f59e0b", color: "#0f172a" };
  }
  // 70-89%: Light green
  if (percentage <= 89) {
    return { bg: "#d1fae5", border: "#6ee7b7", color: "#064e3b" };
  }
  // 90-100%: Deep green
  return { bg: "#059669", border: "#047857", color: "#ffffff" };
}

export default function TaskHeatmap({ onCellClick }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("6");
  const [searchText, setSearchText] = useState("");
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedTier, setSelectedTier] = useState(null); // { min, max } or null for all
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (perfCycleState?.id && positionState?.id) {
      fetchHeatmap();
    }
  }, [perfCycleState, positionState]);

  async function fetchHeatmap() {
    setLoading(true);
    const response = await GetTaskHeatmapApi(
      perfCycleState.id,
      positionState.id
    );
    if (response.success) {
      setData(response.data || []);
    }
    setLoading(false);
  }

  const visibleMonths = useMemo(() => {
    const currentMonth = selectedYear === moment().year() ? moment().month() : 11;
    let count = 12;
    if (range === "3") count = 3;
    else if (range === "6") count = 6;

    if (count === 12) {
      return Array.from({ length: 12 }, (_, i) => i);
    }

    const months = [];
    for (let i = count - 1; i >= 0; i--) {
      const m = currentMonth - i;
      months.push(m < 0 ? m + 12 : m);
    }
    return months;
  }, [range, selectedYear]);

  const filteredData = useMemo(() => {
    let result = data;
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter((row) => {
        const fullName = `${row.name || ""} ${row.lastName || ""}`.toLowerCase();
        const designation = (row.designation || "").toLowerCase();
        return fullName.includes(lower) || designation.includes(lower);
      });
    }
    if (selectedTier) {
      // Only show rows that have at least one visible month cell within the selected tier range
      result = result.filter((row) => {
        return visibleMonths.some((mIdx) => {
          const monthData = row.months[mIdx + 1];
          if (!monthData || monthData.total === 0) return false;
          const pct = monthData.percentage;
          return pct >= selectedTier.min && pct <= selectedTier.max;
        });
      });
    }
    return result;
  }, [data, searchText, selectedTier, visibleMonths]);

  if (loading) {
    return <PageLoader />;
  }

  if (data.length === 0) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: 300 }}>
        <div className="text-center">
          <div className="text-3xl text-gray-400"><WarningOutlined /></div>
          <div className="text-lg pri-font text-gray-400">No heatmap data available</div>
          <div className="text-gray-400 text-sm">Tasks need to be assigned to plans in this performance cycle</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h3 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0, letterSpacing: "-0.3px" }}>
            Monthly Task Completion Heatmaps
          </h3>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, maxWidth: 480 }}>
            Comprehensive overview of user task completion percentages by month across organizational departments.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <Input
            placeholder="Search user or role..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 190, borderRadius: 8, borderColor: "#e2e8f0" }}
            size="small"
            allowClear
          />
        </div>
      </div>

      {/* Range Filter Tabs */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, backgroundColor: "#f1f5f9", borderRadius: 6, padding: 3 }}>
          {[
            { val: "3", label: "Last 3 Months" },
            { val: "6", label: "Last 6 Months" },
            { val: "12", label: "Full Year" },
          ].map(({ val, label }) => {
            const isActive = range === val;
            return (
              <div
                key={val}
                onClick={() => setRange(val)}
                style={{
                  padding: "6px 16px",
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 400,
                  borderRadius: 4,
                  cursor: "pointer",
                  backgroundColor: isActive ? "#fff" : "transparent",
                  color: isActive ? "#ea580c" : "#64748b",
                  boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.15s ease",
                }}
              >
                {label}
              </div>
            );
          })}
          {/* Year selector */}
          <Select
            value={selectedYear}
            onChange={(val) => setSelectedYear(val)}
            size="small"
            bordered={false}
            style={{ width: 80, marginLeft: 4, display: "flex", alignItems: "center" }}
          >
            {[moment().year() - 1, moment().year(), moment().year() + 1].map((y) => (
              <Select.Option key={y} value={y}>{y}</Select.Option>
            ))}
          </Select>
        </div>
      </div>

      {/* Tier Legend */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 18,
          padding: "12px 18px",
          backgroundColor: "#fafbfc",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.6px", marginRight: 4 }}>
          Task Completion Target Tiers:
        </span>
        {[
          { label: "0-9%", min: 0, max: 9, bg: "#991b1b", border: "#7f1d1d", dot: "#fff", textColor: "#fff" },
          { label: "10-19%", min: 10, max: 19, bg: "#dc2626", border: "#b91c1c", dot: "#fff", textColor: "#fff" },
          { label: "20-29%", min: 20, max: 29, bg: "#ef4444", border: "#dc2626", dot: "#fff", textColor: "#fff" },
          { label: "30-39%", min: 30, max: 39, bg: "#fca5a5", border: "#f87171", dot: "#7f1d1d", textColor: "#7f1d1d" },
          { label: "40-59%", min: 40, max: 59, bg: "#fef3c7", border: "#fde68a", dot: "#d97706", textColor: "#78350f" },
          { label: "60-69%", min: 60, max: 69, bg: "#fbbf24", border: "#f59e0b", dot: "#0f172a", textColor: "#0f172a" },
          { label: "70-89%", min: 70, max: 89, bg: "#d1fae5", border: "#6ee7b7", dot: "#059669", textColor: "#064e3b" },
          { label: "90-100%", min: 90, max: 100, bg: "#059669", border: "#047857", dot: "#fff", textColor: "#fff" },
        ].map((tier) => {
          const isActive = selectedTier && selectedTier.min === tier.min && selectedTier.max === tier.max;
          return (
            <div
              key={tier.label}
              onClick={() => setSelectedTier(isActive ? null : { min: tier.min, max: tier.max })}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "4px 10px",
                borderRadius: 14,
                backgroundColor: tier.bg,
                border: `1px solid ${tier.border}`,
                color: tier.textColor,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                opacity: selectedTier && !isActive ? 0.4 : 1,
                transform: isActive ? "scale(1.1)" : "scale(1)",
                boxShadow: isActive ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: tier.dot }} />
              {tier.label}
            </div>
          );
        })}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: 14,
            backgroundColor: "#f8fafc",
            border: "1px dashed #cbd5e1",
            color: "#94a3b8",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#cbd5e1" }} />
          No tasks
        </div>
      </div>

      {/* Heatmap Table */}
      <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#0f172a" }}>
              <th
                style={{
                  textAlign: "left",
                  padding: "14px 18px",
                  minWidth: 220,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#fff",
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  borderRight: "1px solid #1e293b",
                }}
              >
                User
              </th>
              {visibleMonths.map((mIdx) => (
                <th
                  key={mIdx}
                  style={{
                    textAlign: "center",
                    padding: "14px 6px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#fff",
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    minWidth: 80,
                    borderRight: "1px solid #1e293b",
                  }}
                >
                  {MONTHS[mIdx]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, rowIdx) => {
              const fullName = `${row.name || ""} ${row.lastName || ""}`.trim();
              const initials = fullName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

              return (
                <tr
                  key={row.userId || row.positionId || rowIdx}
                  className="heatmap-row"
                  style={{
                    backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#f8fafc",
                    borderBottom: "1px solid #f1f5f9",
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f1f5f980"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = rowIdx % 2 === 0 ? "#ffffff" : "#f8fafc"; }}
                >
                  <td style={{ padding: "12px 18px", verticalAlign: "middle", borderRight: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {/* Profile Avatar */}
                      {row.imageUrl ? (
                        <img
                          src={row.imageUrl}
                          alt={fullName}
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                            border: "2px solid #fff",
                            boxShadow: "0 0 0 1px #e2e8f0",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: "#0f172a",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#fff",
                            fontFamily: "monospace",
                            flexShrink: 0,
                            border: "2px solid #fff",
                            boxShadow: "0 0 0 1px #e2e8f0",
                          }}
                        >
                          {initials}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a", lineHeight: 1.3 }}>
                          {fullName}
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.2 }}>
                          {row.designation}
                        </div>
                      </div>
                    </div>
                  </td>
                  {visibleMonths.map((mIdx) => {
                    const monthData = row.months[mIdx + 1];
                    const isEmpty = monthData === null;

                    if (isEmpty) {
                      return (
                        <td key={mIdx} style={{ padding: "6px 5px", textAlign: "center", borderRight: "1px solid #f1f5f9" }}>
                          <div
                            style={{
                              height: 36,
                              borderRadius: 8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              color: "#cbd5e1",
                            }}
                          >
                            –
                          </div>
                        </td>
                      );
                    }

                    if (monthData.total === 0) {
                      return (
                        <td key={mIdx} style={{ padding: "6px 5px", textAlign: "center", borderRight: "1px solid #f1f5f9" }}>
                          <div
                            style={{
                              height: 36,
                              borderRadius: 8,
                              backgroundColor: "#f8fafc",
                              border: "1px dashed #cbd5e1",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              color: "#94a3b8",
                            }}
                          >
                            –
                          </div>
                        </td>
                      );
                    }

                    const pct = monthData.percentage;
                    const styles = getCellStyles(pct);
                    const matchesTier = !selectedTier || (pct >= selectedTier.min && pct <= selectedTier.max);

                    return (
                      <td key={mIdx} style={{ padding: "6px 5px", textAlign: "center", borderRight: "1px solid #f1f5f9" }}>
                        <Popover
                          content={
                            <div style={{ fontSize: 12, minWidth: 160 }}>
                              <div style={{ fontWeight: 700, marginBottom: 6, color: "#0f172a" }}>
                                {fullName}
                              </div>
                              <div style={{ color: "#64748b", marginBottom: 2 }}>
                                {MONTHS[mIdx]} — {monthData.completed}/{monthData.total} tasks
                              </div>
                              <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
                                {pct}% completion rate
                              </div>
                            </div>
                          }
                          trigger="hover"
                        >
                          <div
                            style={{
                              height: 36,
                              borderRadius: 8,
                              backgroundColor: styles.bg,
                              border: `1px solid ${styles.border}`,
                              color: styles.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              fontWeight: 700,
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                              cursor: onCellClick ? "pointer" : "default",
                              transition: "transform 0.2s ease, opacity 0.2s ease",
                              opacity: matchesTier ? 1 : 0.15,
                            }}
                            onClick={() => {
                              if (onCellClick) {
                                onCellClick({ userId: row.userId, positionIds: row.positionIds || [], monthIdx: mIdx, year: selectedYear });
                              }
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.04)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                          >
                            {pct}%
                          </div>
                        </Popover>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
